import React, { useState } from "react";
import { Button } from "reactstrap";
import { toast } from "react-toastify";

import CourseForm from "./CourseForm";
import SkillsForm from "./skillsform";
import KeySummaryForm from "./keysummary";
import SyllabusForm from "./Syllabusform";

interface CourseTabsProps {
  validation: any;
  isEdit?: boolean;
  onClose?: () => void;
}

const TABS = ["Basic Info", "Syllabus", "Add Skills", "Key Summary"];

const TAB_REQUIRED_FIELDS: Record<string, string[]> = {
  "Basic Info": [
    "course_name",
    "trainer",
    "description",
    "duration",
    "course_level",
    "price",
    "thumbnail_url",
    "noofmodule",
    "hourperday",
    "noofweek",
  ],
  "Syllabus": ["key_deliverables", "daywise_description"],
  // Add Skills is optional now, so we do not enforce required fields here.
  "Add Skills": [],
  "Key Summary": ["summaries"],
};

const CourseTabs: React.FC<CourseTabsProps> = ({ validation, isEdit, onClose }) => {
  const [activeTab, setActiveTab] = useState<string>("Basic Info");
  const [loading, setLoading] = useState(false);

  const values = validation.values;

  // 👉 Function to move to next tab
  const goToNextTab = () => {
    const current = TABS.indexOf(activeTab);
    if (current < TABS.length - 1) {
      setActiveTab(TABS[current + 1]);
    }
  };

  const handleStepSubmit = async () => {
    setLoading(true);

    const errors = await validation.validateForm();
    Object.keys(errors).forEach((field) => {
      validation.setFieldTouched(field, true, false);
    });

    const requiredFields = TAB_REQUIRED_FIELDS[activeTab] || [];
    const hasErrors = requiredFields.some((field) => {
      const value = values[field];
      const error = errors[field];
      const isEmptyArray = Array.isArray(value) ? value.length === 0 : !value;

      return isEmptyArray || !!error;
    });

    if (hasErrors) {
      // Do not show the generic toast on the Add Skills tab per request.
      if (activeTab !== "Add Skills") {
        toast.error("Please fill all required fields.");
      }
      setLoading(false);
      return;
    }

    const current = TABS.indexOf(activeTab);
    if (current < TABS.length - 1) {
      setActiveTab(TABS[current + 1]);
    } else {
      toast.success("Form Completed!");
      if (onClose) onClose();
    }

    setLoading(false);
  };

  const RenderForm = () => {
    switch (activeTab) {
      case "Basic Info":
        return (
          <CourseForm
            validation={validation}
            isEdit={!!isEdit}
            onSuccessNext={goToNextTab} 
          />
        );
      case "Syllabus":
       return (
        <SyllabusForm
          validation={validation}   
        />
      );
      case "Add Skills":
        return <SkillsForm validation={validation} courseId={validation.values.course_id} />;

      case "Key Summary":
        return <KeySummaryForm validation={validation} />;

      default:
        return null;
    }
  };

  const isLastTab = activeTab === "Key Summary";

  return (
    <div className="px-3 py-3">
      <div className="d-flex flex-wrap gap-2 mb-3 mt-3">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            style={{
              background: activeTab === tab ? "#174d40" : "#f5f5f5",
              color: activeTab === tab ? "#fff" : "#000",
              borderRadius: "8px",
              border: "none",
              padding: "10px 18px",
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      <RenderForm />

      {activeTab !== "Basic Info" && (
        <div className="d-flex justify-content-end mt-4">
          <Button color="primary" onClick={handleStepSubmit} disabled={loading}>
            {loading ? <i className="fa fa-spinner fa-spin" /> : isLastTab ? "Submit" : "Next"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CourseTabs;
